import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/platform/auth/useAuth'
import { ExperienceEngine } from '../experiences/core/ExperienceEngine'
import {
  Zap,
  TrendingUp,
  Award,
  ArrowRight,
  Shield,
  Cpu,
  CheckCircle,
  FileText,
  Calendar,
  Lock,
  Code
} from 'lucide-react'
import styles from './LandingPage.module.css'

export function LandingPage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()
  
  // Platform Explorer state
  const [activeTab, setActiveTab] = useState<'planner' | 'goals' | 'ai'>('planner')
  const [checklist, setChecklist] = useState([
    { id: 1, text: 'Ship NTTD Experience manifest refactor', done: true },
    { id: 2, text: 'Verify local storage cookie encryption', done: false },
    { id: 3, text: 'Run production bundle size check', done: false },
  ])
  const [explorerStreak, setExplorerStreak] = useState(4)
  const [productivityScore, setProductivityScore] = useState(72)

  // Redirect authenticated users directly to /dashboard
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  const toggleCheck = (id: number) => {
    setChecklist((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const nextState = !item.done
          // Adjust streak & score in explorer sandbox to create immediate interactivity
          if (nextState) {
            setProductivityScore((s) => Math.min(100, s + 9))
            setExplorerStreak((s) => s + 1)
          } else {
            setProductivityScore((s) => Math.max(0, s - 9))
            setExplorerStreak((s) => Math.max(0, s - 1))
          }
          return { ...item, done: nextState }
        }
        return item
      })
    )
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0a0a0b' }}>
        <div style={{ color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>Loading NTTD...</div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      {/* Experience Engine overlay (only runs unauthenticated) */}
      {!isAuthenticated && <ExperienceEngine />}

      {/* Glow ambient panels */}
      <div className={styles.glowBackground} style={{ top: '5%', left: '10%' }} />
      <div className={styles.glowBackground} style={{ top: '40%', right: '5%' }} />

      {/* Sticky Header Nav */}
      <header className={styles.header}>
        <div className={styles.brand}>
          <div className={styles.logo}>
            <Zap size={16} color="#ffffff" strokeWidth={2.5} />
          </div>
          <span className={styles.brandName}>NTTD</span>
        </div>

        <div className={styles.navLinks}>
          <button className={styles.btnLink} onClick={() => navigate('/login')}>
            Sign In
          </button>
          <button className={styles.btnPrimary} onClick={() => navigate('/register')}>
            Get Started
          </button>
        </div>
      </header>

      {/* Hero Header */}
      <section className={styles.hero}>
        <div className={styles.heroTagline}>
          <span>NTTD Ecosystem v1.0</span>
          <ArrowRight size={12} />
        </div>
        <h1 className={styles.heroTitle}>No Time To Delay</h1>
        <p className={styles.heroSubtitle}>
          An execution-first productivity space built to bridge the gap between intention and action.
        </p>
        <div className={styles.heroActions}>
          <button className={styles.btnWhite} onClick={() => navigate('/register')}>
            Enter Ecosystem
          </button>
          <button
            className={styles.btnSecondary}
            onClick={() => document.getElementById('explorer')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Explore Sandbox
          </button>
        </div>
      </section>

      {/* Step 1: The NTTD Manifesto */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>The NTTD Manifesto</h2>
          <p className={styles.sectionSubtitle}>We build systems that reduce latency, prioritizing action over intention.</p>
        </div>

        <div className={styles.manifestoGrid}>
          <div className={styles.manifestoCard}>
            <span className={styles.manifestoNum}>01 // PROGRESS</span>
            <h3 className={styles.manifestoTitle}>Execution Over Intention</h3>
            <p className={styles.manifestoBody}>
              Intention is a list of goals. Execution is logs, commits, and habits completed. We make execution the only true measure of progress.
            </p>
          </div>

          <div className={styles.manifestoCard}>
            <span className={styles.manifestoNum}>02 // PHYSICS</span>
            <h3 className={styles.manifestoTitle}>Momentum Over Motivation</h3>
            <p className={styles.manifestoBody}>
              Motivation is volatile. Momentum is structural. We optimize interfaces to lower the friction of taking the very first step.
            </p>
          </div>

          <div className={styles.manifestoCard}>
            <span className={styles.manifestoNum}>03 // HABIT</span>
            <h3 className={styles.manifestoTitle}>Consistency Over Intensity</h3>
            <p className={styles.manifestoBody}>
              Massive bursts followed by long blocks of delay lead to stagnation. Consistent, daily actions build compound momentum.
            </p>
          </div>

          <div className={styles.manifestoCard}>
            <span className={styles.manifestoNum}>04 // REDUCTION</span>
            <h3 className={styles.manifestoTitle}>Systems Over Goals</h3>
            <p className={styles.manifestoBody}>
              A goal is a target; a system is the operational checklist that gets you there. We design features to help you automate systems.
            </p>
          </div>
        </div>
      </section>

      {/* Step 2: Interactive Sandbox Explorer */}
      <section id="explorer" className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Interactive Sandbox Explorer</h2>
          <p className={styles.sectionSubtitle}>Interact with a live, miniature simulation of platform modules before joining.</p>
        </div>

        <div className={styles.explorerWrapper}>
          <div className={styles.explorerTabs}>
            <button
              className={`${styles.tabBtn} ${activeTab === 'planner' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('planner')}
            >
              <Calendar size={12} /> Daily Planner
            </button>
            <button
              className={`${styles.tabBtn} ${activeTab === 'goals' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('goals')}
            >
              <Award size={12} /> Goals Stream
            </button>
            <button
              className={`${styles.tabBtn} ${activeTab === 'ai' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              <Cpu size={12} /> Neural AI Core
            </button>
          </div>

          <div className={styles.explorerContent}>
            {activeTab === 'planner' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', display: 'flex', gap: '20px' }}>
                  <span>Streak: <strong style={{ color: '#818cf8' }}>{explorerStreak} days</strong></span>
                  <span>Productivity: <strong style={{ color: '#34d399' }}>{productivityScore}/100</strong></span>
                </div>
                <div className={styles.explorerChecklist}>
                  {checklist.map((item) => (
                    <div
                      key={item.id}
                      className={`${styles.checkItem} ${item.done ? styles.checkItemDone : ''}`}
                      onClick={() => toggleCheck(item.id)}
                    >
                      <div className={`${styles.checkbox} ${item.done ? styles.checkboxDone : ''}`}>
                        {item.done && '✓'}
                      </div>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
                <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
                  * Click checklist circles to complete actions, increment streak counters, and recalculate score.
                </span>
              </div>
            )}

            {activeTab === 'goals' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '480px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span>Ecosystem Engine 1.0 Kernel</span>
                    <span style={{ color: '#34d399' }}>80% Completed</span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: '80%', height: '100%', background: '#6366f1' }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                    <span style={{ opacity: 0.6 }}>Mobile Platforms App Store (Q4)</span>
                    <span style={{ color: 'var(--color-text-tertiary)' }}>Locked</span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.04)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: '0%', height: '100%', background: '#6366f1' }} />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'ai' && (
              <div className={styles.synapseCanvas}>
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: '#10b981',
                    boxShadow: '0 0 20px #10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Cpu size={18} color="#ffffff" />
                </div>
                <span
                  style={{
                    position: 'absolute',
                    bottom: '16px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    color: '#34d399',
                  }}
                >
                  synaptic model synchronizing...
                </span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Step 3: Why NTTD Exists (Creator Narrative) */}
      <section className={styles.section}>
        <div className={styles.sectionHeader} style={{ textAlign: 'left' }}>
          <h2 className={styles.sectionTitle}>Why NTTD Exists</h2>
          <p className={styles.sectionSubtitle} style={{ margin: 0, maxWidth: '100%' }}>
            Solving the Intention Gap: The distance between planning and execution.
          </p>
        </div>

        <div className={styles.storyWrap}>
          <p className={styles.storyParagraph}>
            Most productivity software is designed around the concept of planning. They make you build lists, organize boards, tag tickets, and compile milestones. The act of planning feels satisfying—it releases dopamine, creating a false sensation of accomplishment.
          </p>
          <p className={styles.storyParagraph}>
            But plan cards don’t write code. Lists don’t solve bugs.
          </p>
          <p className={styles.storyParagraph}>
            We built NTTD because planning is not the product. **Execution is the product.** NoTimeToDelay provides a zero-latency workspace designed to lower the mental resistance of starting. By gamifying consistency and integrating telemetry scores, we bridge the gap between what you intend to do and what you actually execute.
          </p>
        </div>
      </section>

      {/* Step 4: Engineering Values (Trust Pillars) */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Built on Engineering Integrity</h2>
          <p className={styles.sectionSubtitle}>We build tools based on engineering performance, not marketing claims.</p>
        </div>

        <div className={styles.trustGrid}>
          <div className={styles.trustCard}>
            <h3 className={styles.trustTitle}>
              <CheckCircle size={16} color="#6366f1" /> Zero Latency Speeds
            </h3>
            <p className={styles.trustBody}>
              No slow frames. Built with pure modular CSS and atomic React components to ensure pages render in milliseconds.
            </p>
          </div>

          <div className={styles.trustCard}>
            <h3 className={styles.trustTitle}>
              <Lock size={16} color="#10b981" /> Local-First Privacy
            </h3>
            <p className={styles.trustBody}>
              Your information belongs to you. Caches and history logs are kept encrypted in local application storage keys.
            </p>
          </div>

          <div className={styles.trustCard}>
            <h3 className={styles.trustTitle}>
              <Code size={16} color="#a855f7" /> Open Extensibility
            </h3>
            <p className={styles.trustBody}>
              Long-term framework architecture. Add custom experiences, modules, and schedule plugins through simple manifests.
            </p>
          </div>
        </div>
      </section>

      {/* Step 5: Platform Roadmap */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Ecosystem Roadmap</h2>
          <p className={styles.sectionSubtitle}>The roadmap timeline for the rollout of platform modules.</p>
        </div>

        <div className={styles.roadmapGrid}>
          <div className={styles.roadmapNode}>
            <div className={`${styles.roadmapNodeDot} ${styles.roadmapNodeActive}`} />
            <span className={styles.roadmapLabel}>v1.0 Platform Release (Live)</span>
            <p className={styles.roadmapDesc}>Core Planner, Goals Tracking, and Experience Engine orchestration.</p>
          </div>

          <div className={styles.roadmapNode}>
            <div className={styles.roadmapNodeDot} />
            <span className={styles.roadmapLabel}>Neural AI Assistance (Q3)</span>
            <p className={styles.roadmapDesc}>Integrated synaptic suggestion models and automated priority tagging.</p>
          </div>

          <div className={styles.roadmapNode}>
            <div className={styles.roadmapNodeDot} />
            <span className={styles.roadmapLabel}>Self-Hosting & Mobile Kernels (Q4)</span>
            <p className={styles.roadmapDesc}>Docker deployments and lightweight iOS/Android native focus shell apps.</p>
          </div>
        </div>
      </section>

      {/* Main Footer compliance area */}
      <footer className={styles.footer}>
        <div>© {new Date().getFullYear()} NoTimeToDelay (NTTD). All rights reserved.</div>
        <div className={styles.footerLinks}>
          <button className={styles.btnLink} onClick={() => navigate('/privacy')}>
            Privacy Policy
          </button>
          <button className={styles.btnLink} onClick={() => navigate('/terms')}>
            Terms of Service
          </button>
          <button className={styles.btnLink} onClick={() => navigate('/data-deletion')}>
            Data Deletion
          </button>
          <button className={styles.btnLink} onClick={() => navigate('/contact')}>
            Contact Support
          </button>
        </div>
      </footer>
    </div>
  )
}
export default LandingPage
